/**
 * ApprovalDetail (Round 1.3).
 *
 * Renders inside `<Drawer>`. Shows full approval metadata (asset, requester,
 * risk, required steps, SLA, lifecycle timestamps) and exposes the four
 * mutation actions: approve / reject / withdraw / escalate.
 *
 * Approve/reject route through `/api/v1/approvals/:id/decision` (only enabled
 * if the user is in a reviewer WG for any pending step). Withdraw is the
 * requester's path. Escalate bumps SLA without changing state.
 */
import { useState, type JSX } from 'react';
import { AlertTriangle, BadgeCheck, Clock4, ShieldOff, X } from 'lucide-react';

import {
  ApiError,
  useApprovalDecide,
  useApprovalEscalate,
  useApprovalWithdraw,
  type ApprovalRow,
} from '@/api/index.js';
import { useAuth } from '@/auth/AuthContext.js';

import { Drawer } from '../Drawer.js';

interface ApprovalDetailProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly approval: ApprovalRow | null;
}

export function ApprovalDetail({ open, onClose, approval }: ApprovalDetailProps): JSX.Element {
  const { me } = useAuth();
  const decide = useApprovalDecide();
  const withdraw = useApprovalWithdraw();
  const escalate = useApprovalEscalate();
  const [notes, setNotes] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  if (!approval) {
    return (
      <Drawer open={open} onClose={onClose} eyebrow="Approval" title="Loading…">
        <p className="subtle">No approval selected.</p>
      </Drawer>
    );
  }

  const isRequester = me?.email != null && approval.requesterSub === me.email;
  const isAdmin = me?.is_admin === true;
  const canDecide = isAdmin && (approval.state === 'pending' || approval.state === 'in_review');
  const canWithdraw =
    (isRequester || isAdmin) && (approval.state === 'pending' || approval.state === 'in_review');
  const canEscalate = isAdmin && approval.state === 'in_review';

  function handleResult(promise: Promise<unknown>): void {
    setActionError(null);
    promise
      .then(() => {
        setNotes('');
        onClose();
      })
      .catch((err: unknown) => {
        setActionError(
          err instanceof ApiError
            ? `${err.status}: ${JSON.stringify(err.body)}`
            : (err as Error).message,
        );
      });
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow="Approval"
      title={`${approval.assetType} · ${approval.assetId}`}
      footer={
        <>
          {canWithdraw && (
            <button
              type="button"
              className="danger"
              onClick={() => handleResult(withdraw.mutateAsync(approval.id))}
              disabled={withdraw.isPending}
            >
              <X size={14} aria-hidden /> Withdraw
            </button>
          )}
          {canEscalate && (
            <button
              type="button"
              onClick={() => handleResult(escalate.mutateAsync(approval.id))}
              disabled={escalate.isPending}
            >
              <Clock4 size={14} aria-hidden /> Escalate
            </button>
          )}
          {canDecide && (
            <>
              <button
                type="button"
                className="danger"
                onClick={() =>
                  handleResult(
                    decide.mutateAsync({
                      approvalId: approval.id,
                      decision: 'rejected',
                      notes: notes || undefined,
                    }),
                  )
                }
                disabled={decide.isPending}
              >
                <ShieldOff size={14} aria-hidden /> Reject
              </button>
              <button
                type="button"
                onClick={() =>
                  handleResult(
                    decide.mutateAsync({
                      approvalId: approval.id,
                      decision: 'changes_requested',
                      notes: notes || undefined,
                    }),
                  )
                }
                disabled={decide.isPending || !notes.trim()}
                title={!notes.trim() ? 'Add notes describing the requested changes' : undefined}
              >
                Request changes
              </button>
              <button
                type="button"
                className="primary"
                onClick={() =>
                  handleResult(
                    decide.mutateAsync({
                      approvalId: approval.id,
                      decision: 'approved',
                      notes: notes || undefined,
                    }),
                  )
                }
                disabled={decide.isPending}
              >
                <BadgeCheck size={14} aria-hidden /> Approve
              </button>
            </>
          )}
        </>
      }
    >
      <div className="drawer-stack">
        <KV label="ID" value={<code>{approval.id}</code>} />
        <KV label="State" value={<strong>{approval.state}</strong>} />
        <KV label="Risk tier" value={approval.riskTier} />
        <KV label="Requester" value={<code>{approval.requesterSub}</code>} />
        <KV
          label="Requester WGs"
          value={
            approval.requesterWgSnapshot.length > 0
              ? approval.requesterWgSnapshot.join(', ')
              : '(none on submit)'
          }
        />
        {approval.riskNotes && <KV label="Risk notes" value={approval.riskNotes} />}
        <KV
          label="SLA due"
          value={
            approval.slaDueAt
              ? `${new Date(approval.slaDueAt).toLocaleString()} (${relativeFromNow(approval.slaDueAt)})`
              : 'no SLA'
          }
        />
        <KV label="Created" value={new Date(approval.createdAt).toLocaleString()} />
        <KV label="Updated" value={new Date(approval.updatedAt).toLocaleString()} />
        {approval.decidedAt && (
          <KV label="Decided" value={new Date(approval.decidedAt).toLocaleString()} />
        )}

        <div>
          <h3 style={{ margin: '14px 0 6px', fontSize: 15 }}>Required steps</h3>
          {approval.requiredSteps.length === 0 ? (
            <p className="subtle">No reviewer steps configured.</p>
          ) : (
            <ol style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.55 }}>
              {approval.requiredSteps.map((step, idx) => (
                <li key={`${step.step}-${idx}`} style={{ marginBottom: 6 }}>
                  <strong>{step.step}</strong> — reviewer WG <code>{step.reviewer_wg_id}</code> ·{' '}
                  {step.decided ? step.decision : 'pending'}
                  {step.notes && (
                    <p className="subtle" style={{ margin: '2px 0 0', fontSize: 13 }}>
                      {step.notes}
                    </p>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>

        {canDecide && (
          <label style={{ display: 'block', marginTop: 12 }}>
            <span style={{ display: 'block', fontSize: 14, marginBottom: 4 }}>
              Decision notes (optional)
            </span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              style={{
                width: '100%',
                padding: 8,
                border: '1px solid var(--line)',
                borderRadius: 8,
                fontSize: 14,
                background: 'transparent',
                color: 'inherit',
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
          </label>
        )}

        {actionError && (
          <p style={{ color: '#c0392b', fontSize: 14 }} role="alert">
            <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {actionError}
          </p>
        )}
      </div>
    </Drawer>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div
      style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, max-content) 1fr', gap: 12 }}
    >
      <span className="subtle" style={{ fontSize: 13 }}>
        {label}
      </span>
      <span style={{ fontSize: 14 }}>{value}</span>
    </div>
  );
}

function relativeFromNow(iso: string): string {
  const ms = Date.parse(iso) - Date.now();
  if (Number.isNaN(ms)) return iso;
  if (ms < 0) return 'overdue';
  const hrs = Math.round(ms / 3_600_000);
  if (hrs < 1) return `${Math.round(ms / 60_000)}m`;
  if (hrs < 24) return `${hrs}h`;
  return `${Math.round(hrs / 24)}d`;
}
